import clsx from 'clsx'
import HeadingDecoration from '@Components/ViewWork/Table/TableHeadingDecoration'
import React, { ReactNode, useEffect, useLayoutEffect, useRef } from 'react'
import { FieldValues, FormProvider, Resolver, useForm, UseFormReset } from 'react-hook-form'
import AddPlaceButton from '@Components/Buttons/AddPlaceButton'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import Table, { TableInfo } from '@Components/ViewWork/Table'
import { IconType } from '@Const/common'
import TableGlobalError from '@Components/ViewWork/Table/TableGlobalError'

interface HeadingDecorationProps {
    icon: IconType
    title: string
    toolTip?: ReactNode
    required?: boolean
    rightData?: ReactNode
}

interface TableFormProps {
    className?: string
    headingDecoration?: HeadingDecorationProps
    tableInfos: TableInfo[]
    footerRow?: ReactNode[]
    addButtonTitle?: string
    hideAddButton?: boolean
    onAddButtonClicked?: () => void
    onRemoveCellClicked?: (
        index: number,
        removeCellIdentifier?: string,
        reset?: UseFormReset<FieldValues>,
        values?: FieldValues
    ) => void
    validationSchema?: z.Schema<any, any>
    rowsAreValid?: (isValid: boolean) => void
    onFormSubmitted?: (data: any) => void
    shouldFocusError?: boolean
    useAlternateHeader?: boolean
}

const TablesForm = ({
    className,
    headingDecoration,
    tableInfos,
    footerRow,
    addButtonTitle,
    hideAddButton,
    onAddButtonClicked,
    validationSchema,
    rowsAreValid,
    onFormSubmitted,
    onRemoveCellClicked,
    shouldFocusError = false,
    useAlternateHeader,
}: TableFormProps) => {
    const resolverOptions = (): { resolver?: Resolver<FieldValues> } => {
        if (validationSchema) {
            return {
                resolver: zodResolver(
                    z.object({
                        rows: validationSchema,
                    })
                ) as unknown as Resolver<FieldValues>,
            }
        }
        return {}
    }

    const methods = useForm<FieldValues>({
        ...resolverOptions(),
        mode: 'all',
        reValidateMode: 'onChange',
        shouldUnregister: true,
        shouldFocusError: shouldFocusError,
    })

    const onSubmit = (data: any) => {
        onFormSubmitted && onFormSubmitted(data)
    }

    useEffect(() => {
        const callback = methods.subscribe({
            formState: {
                values: true,
            },
            callback: () => {
                methods.handleSubmit(onSubmit)()
            },
        })

        return () => callback()
    }, [methods.subscribe, methods.handleSubmit, onSubmit])

    const tableInfoRowCount = tableInfos.map((tableInfo) => tableInfo.rows.length).reduce((a, b) => a + b, 0)

    useEffect(() => {
        rowsAreValid && rowsAreValid(methods.formState.isValid)
    }, [methods.formState.isValid, rowsAreValid])

    const previousTableInfoRowCount = useRef<number | undefined>(undefined)

    // Forces revalidation when data is modified outside of form
    useLayoutEffect(() => {
        if (previousTableInfoRowCount.current !== tableInfoRowCount) {
            previousTableInfoRowCount.current = tableInfoRowCount
            methods.trigger().then()
        }
    }, [methods.trigger, tableInfoRowCount])

    const removeCellClicked = (removedCellIndex: number, removeCellIdentifier?: string) => {
        onRemoveCellClicked &&
            onRemoveCellClicked(removedCellIndex, removeCellIdentifier, methods.reset, methods.getValues())
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} className={clsx('table-overflow-container', className)}>
                <div className="table-layout">
                    {headingDecoration && (
                        <HeadingDecoration
                            title={headingDecoration.title}
                            icon={headingDecoration.icon}
                            toolTip={headingDecoration.toolTip}
                            required={headingDecoration.required}
                            rowLength={tableInfoRowCount}
                            useAlternateHeader={useAlternateHeader}
                            rightData={headingDecoration.rightData}
                        />
                    )}
                    {tableInfos.map((tableInfo, tableInfoIndex) => (
                        <Table
                            headerRow={tableInfo.headerRow}
                            rows={tableInfo.rows}
                            footerRow={footerRow}
                            onRemoveCells={(removedCellIndex, removeCellIdentifier) =>
                                removeCellClicked(removedCellIndex, removeCellIdentifier)
                            }
                            key={tableInfoIndex}
                        />
                    ))}
                    <div className="add-place-button-container">
                        {!hideAddButton && (
                            <AddPlaceButton title={addButtonTitle ?? 'ADD'} onClick={onAddButtonClicked} />
                        )}
                        <TableGlobalError />
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

export default TablesForm
